import { useNavigate } from 'react-router-dom';
import Button from '../components/button/Button';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import { routes } from '../routes/routePaths';
import { useEffect, useRef, useState } from 'react';
import { getApplications } from '../services/ApplicationService';
import P from '../components/texts/p/P';
import Image from '../components/image/Image';
import Table from '../components/table/table';
import Input from '../components/input/Input';
import { IItem, ITableData } from '../types/interfaces';
import { hasContent, normalizeCuitForSearch } from '../utils/Utils';
import { getDashboardColumns } from '../utils/dashboardColumns';
import TableSkeleton from '../components/TableSkeleton/TableSkeleton';
import { useDebounce } from 'use-debounce';
import { useOfficers } from '../hooks/useOfficers';
import useRole from '../hooks/useRole';
import Icon from '../components/icon/Icon';
import FilterModal from '../components/modals/FilterModal';
import { APPLICATION_STATUS } from '../utils/Constants';
import { getFilters } from '../services/ApplicationService';
import { FilterData } from '../components/modals/FilterModal/FilterModal.types';
import {
  getStatusText,
  isFinishedOrRejectedStatus,
} from '../utils/applicationStatusUtils';
import { useConfig } from '../context/ConfigContext';
import {
  commercialCellFromCommercialEmployee,
  commercialCellFromOfficerPath,
} from '../utils/dashboardListCommercialCell';
import { useRestoreListState } from '../hooks/useRestoreListState';
import { buildListState, DASHBOARD_KEYS } from '../utils/listNavigationState';
import { resolveLocalCurrencyCode } from '../utils/quotationAssets';

type application = {
  cuit: string;
  producerName: string;
  producerLastName: string;
  companyName: string;
  status: string;
};

// Estados permitidos para roles no-productor en originación
const ALLOWED_ORIGINATION_STATUSES = [
  APPLICATION_STATUS.pending.label,    // 'PEN'
  APPLICATION_STATUS.revision.label,   // 'REV'
];

const OriginationDashboard = () => {
  const navigate = useNavigate();
  const name = localStorage.getItem('name');
  const { officers } = useOfficers();
  const { isProducer } = useRole();
  const appConfig = useConfig();
  const isManagementSelectionEnabled = appConfig.isManagementSelectionEnabled ?? false;
  const restoredList = useRestoreListState(DASHBOARD_KEYS.origination);
  const [applications, setApplications] = useState<application[]>([]);
  const [tableData, setTableData] = useState<ITableData>();
  const [page, setPage] = useState<number>(restoredList.page);
  const [searchText, setSearchText] = useState<string>(restoredList.searchText);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [debouncedSearchText] = useDebounce(searchText, 300);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>(
    restoredList.activeFilters,
  );
  const [filterData, setFilterData] = useState<FilterData | null>(null);
  const [isClearFiltersHovered, setIsClearFiltersHovered] =
    useState<boolean>(false);

  const lastRequest = useRef(0);

  // Función para obtener los parámetros de búsqueda según rol y filtros activos
  const getSearchParams = () => {
    let statusesToSearch = isProducer ? [] : ALLOWED_ORIGINATION_STATUSES;
    let filtersToUse = activeFilters;

    // Si hay un filtro de estado aplicado, usar solo ese
    if (activeFilters.status) {
      const statusId = activeFilters.status;
      // Buscar por code (ID numérico) ya que activeFilters.status es un ID como string
      const status = Object.values(APPLICATION_STATUS).find(
        (s) => s.code === parseInt(statusId, 10)
      );
      const resolvedStatus = status ? status.label : statusId;
      // Validar que el estado seleccionado esté permitido para no-productores
      if (isProducer || ALLOWED_ORIGINATION_STATUSES.includes(resolvedStatus)) {
        statusesToSearch = [resolvedStatus];
      } else {
        // Si el estado no está permitido, usar los estados por defecto
        statusesToSearch = ALLOWED_ORIGINATION_STATUSES;
      }
      // Remover el filtro de estado de activeFilters ya que se usa en statuses
      const { status: _, ...filtersWithoutStatus } = activeFilters;
      filtersToUse = filtersWithoutStatus;
    }

    return { statusesToSearch, filtersToUse };
  };

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const response = await getFilters('application');
        if (response.success && 'data' in response) {
          setFilterData(response.data);
        }
      } catch (error) {
        console.error('Error loading filters:', error);
      }
    };
    loadFilters();
  }, []);

  useEffect(() => {
    setIsLoading(true);
    const requestId = Date.now();
    lastRequest.current = requestId;

    const { statusesToSearch, filtersToUse } = getSearchParams();

    // Limpiar el texto de búsqueda para remover guiones de CUITs
    getApplications(page, statusesToSearch, normalizeCuitForSearch(debouncedSearchText), filtersToUse)
      .then((response) => {
        if (lastRequest.current !== requestId) return;
        if (hasContent(response)) {
          setTableData({
            size: response.data.size,
            page: response.data.page,
            totalElements: response.data.totalElements,
            totalPages: response.data.totalPages + 1,
          });

          const newApplications = response.data.content.map((item: IItem) => ({
            cuit: item.cuit,
            id: item.id,
            commercial: commercialCellFromOfficerPath(
              item,
              officers,
              isManagementSelectionEnabled,
            ),
            companyName: item.company_name,
            producerName: item.producer.first_name,
            producerLastName: item.producer.last_name,
            updateDate: item.updated_date,
            status: item.status.id,
            statusCode: item.status.code,
            totalArs: item.total_ars,
            totalUsd: item.total_usd,
            approved_total_ars: item.approved_total_ars,
            approved_total_usd: item.approved_total_usd,
            usuario: item.producer.first_name + ' ' + item.producer.last_name,
            referido: (item as any).referido || 'N/A',
          }));
          setApplications(newApplications);
        } else {
          setApplications([]);
        }
        setIsLoading(false);
        setIsSearching(false);
      })
      .catch(() => {
        setApplications([]);
        setIsLoading(false);
        setIsSearching(false);
      });
  }, [
    page,
    debouncedSearchText,
    JSON.stringify(activeFilters),
    officers,
    isProducer,
    isManagementSelectionEnabled,
  ]);

  const handleChangePage = (data: number) => {
    if (data < 0 || data === tableData?.totalPages) {
      return;
    }
    setPage(data);
  };

  const handleRowClicked = (data: any) => {
    const isFinishedOrRejected = isFinishedOrRejectedStatus(
      data.statusCode ?? data.status
    );
    const listState = buildListState({
      dashboardKey: DASHBOARD_KEYS.origination,
      page,
      searchText,
      activeFilters,
    });

    if (isFinishedOrRejected) {
      navigate(routes.clients + '/' + data.cuit + '/application/' + data.id, {
        state: { status: data.status, statusCode: data.statusCode, listState },
      });
    } else {
      navigate(routes.clients + '/' + data.cuit + '/applications/' + data.id, {
        state: { status: data.status, statusCode: data.statusCode, listState },
      });
    }
  };

  const columns = getDashboardColumns({
    handleRowClicked,
    commercialColumnTitle: isManagementSelectionEnabled ? 'Gerencia' : 'Comercial',
    country: appConfig.country,
    localCurrencyCode: resolveLocalCurrencyCode(appConfig),
  });

  const handleSearch = () => {
    setIsSearching(true);
    const requestId = Date.now();
    lastRequest.current = requestId;

    const { statusesToSearch, filtersToUse } = getSearchParams();

    // Limpiar el texto de búsqueda para remover guiones de CUITs
    getApplications(page, statusesToSearch, normalizeCuitForSearch(debouncedSearchText), filtersToUse)
      .then((response) => {
        if (lastRequest.current !== requestId) return;
        if (hasContent(response)) {
          setTableData({
            size: response.data.size,
            page: response.data.page,
            totalElements: response.data.totalElements,
            totalPages: response.data.totalPages + 1,
          });

          let content = response.data.content;

          const newApplications = content.map((item: IItem) => ({
            cuit: item.cuit,
            id: item.id,
            commercial: commercialCellFromCommercialEmployee(item),
            companyName: item.company_name,
            producerName: item.producer.first_name,
            producerLastName: item.producer.last_name,
            updateDate: item.updated_date,
            status: item.status.id,
            statusCode: item.status.code,
            totalArs: item.total_ars,
            totalUsd: item.total_usd,
            approved_total_ars: item.approved_total_ars,
            approved_total_usd: item.approved_total_usd,
            usuario: item.producer.first_name + ' ' + item.producer.last_name,
            referido: (item as any).referido || 'N/A',
          }));

          setApplications(newApplications);
        } else {
          setApplications([]);
        }
        setIsLoading(false);
        setIsSearching(false);
      })
      .catch(() => {
        setApplications([]);
        setIsLoading(false);
        setIsSearching(false);
      });
  };

  const handleOpenModal = () => {
    setIsFilterModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsFilterModalOpen(false);
  };

  const handleApplyFilters = (queryParams: Record<string, string>) => {
    setActiveFilters(queryParams);
    setPage(0); // Resetear a la primera página cuando se aplican filtros
  };

  const handleClearFilters = () => {
    setActiveFilters({});
    setPage(0);
  };

  const getActiveFilterLabels = (): Array<{
    type: string;
    label: string;
    filterKey: string;
  }> => {
    if (!filterData || Object.keys(activeFilters).length === 0) {
      return [];
    }

    const labels: Array<{ type: string; label: string; filterKey: string }> =
      [];

    if (activeFilters.commercialId) {
      const commercial = filterData.commercials.find(
        (c) => c.id.toString() === activeFilters.commercialId
      );
      if (commercial) {
        labels.push({
          type: 'Comercial',
          label: commercial.name,
          filterKey: 'commercialId',
        });
      } else {
        labels.push({
          type: 'Comercial',
          label: 'Desconocido',
          filterKey: 'commercialId',
        });
      }
    }

    if (activeFilters.managementId) {
      const idLookup =
        activeFilters.managementId === 'null'
          ? '-1'
          : activeFilters.managementId;
      const mgmt = filterData.managements?.find((m) => m.id === idLookup);
      if (mgmt) {
        labels.push({
          type: 'Gerencia',
          label: mgmt.name,
          filterKey: 'managementId',
        });
      } else {
        labels.push({
          type: 'Gerencia',
          label: 'Desconocido',
          filterKey: 'managementId',
        });
      }
    }

    if (activeFilters.producerId) {
      const producer = filterData.producers.find(
        (p) => p.id.toString() === activeFilters.producerId
      );
      if (producer) {
        labels.push({
          type: 'Usuario',
          label: producer.name,
          filterKey: 'producerId',
        });
      } else {
        labels.push({
          type: 'Usuario',
          label: 'Desconocido',
          filterKey: 'producerId',
        });
      }
    }

    if (activeFilters.referrerName) {
      const referrer = filterData.referrers.find(
        (r) => r.id.toString() === activeFilters.referrerName
      );
      if (referrer) {
        labels.push({
          type: 'Referido/Vendedor',
          label: referrer.name,
          filterKey: 'referrerName',
        });
      } else {
        labels.push({
          type: 'Referido/Vendedor',
          label: 'Desconocido',
          filterKey: 'referrerName',
        });
      }
    }

    if (activeFilters.status) {
      const status = filterData.status.find(
        (s) => s.id.toString() === activeFilters.status
      );
      if (status) {
        const statusObj = Object.values(APPLICATION_STATUS).find(
          (s) => s.code === status.id
        );
        const statusLabel = statusObj ? statusObj.text : status.name;
        labels.push({
          type: 'Estado',
          label: statusLabel,
          filterKey: 'status',
        });
      } else {
        labels.push({
          type: 'Estado',
          label: 'Desconocido',
          filterKey: 'status',
        });
      }
    }

    return labels;
  };

  const handleRemoveFilter = (filterKey: string) => {
    const newFilters = { ...activeFilters };
    delete newFilters[filterKey];
    setActiveFilters(newFilters);
    setPage(0);
  };

  return (
    <Card
      variant='neutral'
      modifier='inverted'
      spacing={'4'}
      height='-webkit-fill-available'
      width='85vw'
    >
      <Card display='flex' justifyContent='space-between'>
        <Card display='flex' alignItems='center'>
          <H withMargin={false} type='1' weight='bold' modifier='strong'>
            Hola, {name}
          </H>
        </Card>
        <Button variant='secondary' onClick={() => navigate(routes.clients)}>
          Alta de nueva carpeta
        </Button>
      </Card>
      <Card display='flex' gap='16px' alignItems='center' margin={{ top: '4' }}>
        <Button variant='secondary-1' onClick={handleOpenModal}>
          <Card display='flex' alignItems='center' gap='8px'>
            <Icon name='other_filter' variant='secondary-1' width={20} height={20} />
            <P variant='secondary-1' withMargin={false}>Editar filtros</P>
          </Card>
        </Button>
        <Card width='400px' display='flex' gap='16px'>
          <Input
            type='text'
            size='md'
            icons={{ iconLeft: 'search_outline' }}
            placeholder='Buscar cliente'
            onChange={(e) => setSearchText(e.target.value)}
            onKeyup={() => handleSearch()}
          />
        </Card>
      </Card>
      {Object.keys(activeFilters).length > 0 && (
        <div
          style={{
            display: 'flex',
            gap: '16px',
            alignItems: 'center',
            marginTop: '8px',
            flexWrap: 'wrap',
            backgroundColor: '#F5F7FA',
            padding: '16px 24px',
            borderRadius: '4px',
            border: '1px solid #E5E7EB',
          }}
        >
          {getActiveFilterLabels().map((filter, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {filter.filterKey === 'status' ? (
                <>
                  <P variant='neutral' withMargin={false} type='3' weight='bold'>
                    {filter.type}
                  </P>
                  <div
                    style={{
                      backgroundColor: '#A16108',
                      padding: '4px 12px',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      height: '24px',
                    }}
                  >
                    <P
                      variant='neutral'
                      modifier='inverted'
                      withMargin={false}
                      type='3'
                      weight='bold'
                      style={{ color: '#FFFFFF', fontSize: '11px', lineHeight: '16px', letterSpacing: '0.3px' }}
                    >
                      {filter.label.toUpperCase()}
                    </P>
                  </div>
                </>
              ) : (
                <>
                  <Icon
                    name='other_filter'
                    variant='secondary-1'
                    width={16}
                    height={16}
                  />
                  <P variant='neutral' withMargin={false} type='3'>
                    <span style={{ fontWeight: 'bold' }}>{filter.type}</span>{' '}
                    {filter.label}
                  </P>
                </>
              )}
            </div>
          ))}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
            }}
            onMouseEnter={() => setIsClearFiltersHovered(true)}
            onMouseLeave={() => setIsClearFiltersHovered(false)}
            onClick={handleClearFilters}
          >
            <Icon
              name='delete_outline'
              variant='neutral'
              width={20}
              height={20}
              style={{ color: activeFilters.status ? '#A16108' : '#585053' }}
            />
            <P variant='neutral' withMargin={false} type='3' style={{ color: activeFilters.status ? '#A16108' : '#585053' }}>
              Borrar filtros
            </P>
          </div>
        </div>
      )}
      {isSearching || isLoading ? (
        <Card margin={{ top: '4' }}>
          <TableSkeleton rows={10} columns={columns.length} />
        </Card>
      ) : applications.length > 0 ? (
        <Card margin={{ top: '4' }} justifyContent='center' alignItems='center'>
            <Table
              columns={columns}
              data={applications}
              tableData={tableData}
              handleChangePage={(e: any) => handleChangePage(e)}
              onRowClicked={(e: any) => handleRowClicked(e)}
            />
        </Card>
      ) : searchText !== '' ? (
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='53vw'
        >
          <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
            <Image name='folder_empty' />
          </Card>
          <P variant='primary' align='center' withMargin={false} weight='bold'>
            No encontramos resultados.
          </P>
          <P variant='primary' align='center'>
            Podés hacer una nueva búsqueda.
          </P>
        </Card>
      ) : (
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='53vw'
        >
          <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
            <Image name='folder_empty' />
          </Card>
          <P variant='primary' align='center' withMargin={false} weight='bold'>
            No hay carpetas en curso.
          </P>
          <P variant='primary' align='center'>
            Podés crear una nueva.
          </P>
          <Button variant='primary' onClick={() => navigate(routes.clients)}>
            Alta de nueva carpeta
          </Button>
        </Card>
      )}
      
      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={handleCloseModal}
        onApplyFilters={handleApplyFilters}
        showCommercialFilter={!isManagementSelectionEnabled}
        showManagementFilter={isManagementSelectionEnabled}
        showProducerFilter={true}
        showReferrerFilter={true}
        showStatusFilter={true}
        allowedStatusIds={isProducer ? undefined : ALLOWED_ORIGINATION_STATUSES}
        initialFilters={activeFilters}
      />
    </Card>
  );
};

export default OriginationDashboard;
